import { Injectable } from '@angular/core';

import { FsLprFleetRead, plateCoords } from '../api/models';

export interface CanvasDimensions {
  width: number;
  height: number;
}

@Injectable({
  providedIn: 'root',
})
export class PlateImageCropService {
  // Constants for plate image cropping and scaling. Can be fine tuned
  // for different camera angles and resolutions. if image crops are too big or too small
  private static readonly EXTEND_FACTOR_WIDTH = 11.0;
  private static readonly EXTEND_FACTOR_HEIGHT = 14.0;
  private static readonly MAX_CROP_WIDTH = 0.5;
  private static readonly MAX_CROP_HEIGHT = 0.6;
  // Constants for different canvas sizes
  public static readonly CARD_DIMENSIONS: CanvasDimensions = {
    width: 350,
    height: 270,
  };

  public static readonly THUMBNAIL_DIMENSIONS: CanvasDimensions = {
    width: 70,
    height: 40,
  };

  public static readonly DETAILS_DIMENSIONS: CanvasDimensions = {
    width: 688,
    height: 344,
  };

  // Bounding box styling
  private static readonly BOUNDING_BOX_COLOR = '#00FF00'; // Bright green
  private static readonly BOUNDING_BOX_LINE_WIDTH = 1; // Line width in pixels
  public static readonly MIN_BOUNDING_BOX_WIDTH = 0.5;
  public static readonly MIN_BOUNDING_BOX_HEIGHT = 0.5;

  /**
   * Crops and scales a license plate image based on provided coordinates and dimensions.
   *
   * @param plate - The plate data containing image coordinates, bounding box, and image URL
   * @param canvas - The HTML canvas element to draw the cropped image on
   * @param dimensions - Optional canvas dimensions for plate card or details, defaults to CARD_DIMENSIONS
   * @param showBoundingBox - Whether to show the bounding box around the license plate
   * @returns Promise<string> - A promise that resolves to the base64-encoded cropped image data URL
   * @throws Error if required plate data (imageCoordinates, plateBoundingBox, or contextImageUrl) is missing
   * @throws Error if image loading fails
   */
  public cropImage(
    plate: FsLprFleetRead,
    canvas: HTMLCanvasElement,
    dimensions: CanvasDimensions = PlateImageCropService.CARD_DIMENSIONS,
    showBoundingBox: boolean = false,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!plate.imageCoordinates || !plate.plateBoundingBox || !plate.contextImageUrl) {
        reject(new Error('Missing required plate coordinates or image'));
        return;
      }

      const context = canvas.getContext('2d');
      const imageObj = new Image();

      canvas.width = dimensions.width;
      canvas.height = dimensions.height;

      imageObj.onerror = () => reject(new Error('Failed to load image'));
      imageObj.crossOrigin = 'anonymous';

      // Use Fetch API instead of directly setting the image source with no cache to prevent CORS issues
      fetch(plate.contextImageUrl, {
        mode: 'cors',
        cache: 'no-cache',
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load image: ${response.status} ${response.statusText}`);
          }
          return response.blob();
        })
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);

          imageObj.src = objectUrl;

          // Clean up the object URL when the image is loaded since it's no longer needed
          imageObj.onload = () => {
            URL.revokeObjectURL(objectUrl);
            try {
              // Step 1: Normalize plateBoundingBox
              const normalizedBox = this._normalizePlateBoundingBox(
                plate.plateBoundingBox,
                plate.imageCoordinates,
                imageObj.width,
                imageObj.height,
              );

              // Step 2: Extend the box
              const extendedBox = this._calculateExtendedBox(normalizedBox);

              // Convert normalized coordinates back to absolute pixels
              const cropX = extendedBox.x * imageObj.width;
              const cropY = extendedBox.y * imageObj.height;
              const cropWidth = extendedBox.width * imageObj.width;
              const cropHeight = extendedBox.height * imageObj.height;

              // Calculate scaling to maintain aspect ratio
              const cropAspectRatio = cropWidth / cropHeight;
              const canvasAspectRatio = canvas.width / canvas.height;

              let drawWidth, drawHeight, drawX, drawY;
              let sourceCropX, sourceCropY, sourceCropWidth, sourceCropHeight;

              // Clear canvas with black background first (for any remaining empty spaces)
              context.fillStyle = '#000000';
              context.fillRect(0, 0, canvas.width, canvas.height);

              if (cropAspectRatio > canvasAspectRatio) {
                // Crop is wider than canvas - fit to width
                this._adjustForWideFit(
                  cropAspectRatio,
                  canvas,
                  cropX,
                  cropY,
                  cropWidth,
                  cropHeight,
                  imageObj,
                  extendedBox,
                );

                // Get the calculated source parameters from extendedBox
                sourceCropX = extendedBox.x * imageObj.width;
                sourceCropY = extendedBox.y * imageObj.height;
                sourceCropWidth = extendedBox.width * imageObj.width;
                sourceCropHeight = extendedBox.height * imageObj.height;

                // Fit to width - full width, centered height
                drawWidth = canvas.width;
                drawHeight = canvas.width / cropAspectRatio;
                drawX = 0;
                drawY = (canvas.height - drawHeight) / 2;
              } else {
                // Crop is taller than canvas - fit to height
                this._adjustForTallFit(
                  cropAspectRatio,
                  canvas,
                  cropX,
                  cropY,
                  cropWidth,
                  cropHeight,
                  imageObj,
                  extendedBox,
                );

                // Get the calculated source parameters from extendedBox
                sourceCropX = extendedBox.x * imageObj.width;
                sourceCropY = extendedBox.y * imageObj.height;
                sourceCropWidth = extendedBox.width * imageObj.width;
                sourceCropHeight = extendedBox.height * imageObj.height;

                // Fit to height - full height, centered width
                drawHeight = canvas.height;
                drawWidth = canvas.height * cropAspectRatio;
                drawX = (canvas.width - drawWidth) / 2;
                drawY = 0;
              }

              // Draw the image once with the extended dimensions
              context.drawImage(
                imageObj,
                sourceCropX,
                sourceCropY,
                sourceCropWidth,
                sourceCropHeight,
                0,
                0, // Despite calculating drawX and drawY for aspect ratio, we always draw at 0,0
                canvas.width,
                canvas.height, // And always use full canvas dimensions
              );

              if (showBoundingBox) {
                this._drawPlateBoundingBox(
                  context,
                  plate.plateBoundingBox,
                  plate.imageCoordinates,
                  extendedBox,
                  canvas.width,
                  canvas.height,
                  0,
                  0,
                );
              }

              resolve(canvas.toDataURL('image/jpeg'));
            } catch (err) {
              reject(err);
            }
          };
        })
        .catch(reject);
    });
  }

  /**
   * Creates a safe copy of imageCoordinates with fallback values
   * @param imageCoordinates - The original imageCoordinates object
   * @returns A safe copy with fallback values, if the values are not present
   */
  private _createSafeImageCoords(imageCoordinates: plateCoords): plateCoords {
    return {
      ...imageCoordinates,
      x: imageCoordinates.x ?? 0,
      y: imageCoordinates.y ?? 0,
      width: imageCoordinates.width ?? 1,
      height: imageCoordinates.height ?? 1,
    };
  }

  /**
   * Creates a safe copy of plate with fallback values
   * @returns A safe copy with fallback values, if the values are not present
   * @param plateBoundingBox
   */
  private _createSafePlateBoundingBox(plateBoundingBox: plateCoords): plateCoords {
    return {
      ...plateBoundingBox,
      x: plateBoundingBox.x ?? 0,
      y: plateBoundingBox.y ?? 0,
      width:
        plateBoundingBox.width && plateBoundingBox.width !== 0
          ? plateBoundingBox.width
          : PlateImageCropService.MIN_BOUNDING_BOX_WIDTH,
      height:
        plateBoundingBox.height && plateBoundingBox.height !== 0
          ? plateBoundingBox.height
          : PlateImageCropService.MIN_BOUNDING_BOX_HEIGHT,
    };
  }

  /**
   * Draws a green bounding box around the license plate in the cropped image
   *
   * @param context - The canvas 2D context
   * @param plateBoundingBox - Original plate bounding box coordinates
   * @param imageCoordinates - Image coordinates
   * @param extendedBox - Extended crop box coordinates
   * @param drawWidth - Width of the canvas
   * @param drawHeight - Height of the canvas
   * @param drawX - X coordinate of the drawing position
   * @param drawY - Y coordinate of the drawing position
   */
  private _drawPlateBoundingBox(
    context: CanvasRenderingContext2D,
    plateBoundingBox: plateCoords,
    imageCoordinates: plateCoords,
    extendedBox: plateCoords,
    drawWidth: number,
    drawHeight: number,
    drawX: number,
    drawY: number,
  ): void {
    try {
      const safeImageCoords = this._createSafeImageCoords(imageCoordinates);

      // Calculate the relative position of the plate within the extended box
      const relativeX = (plateBoundingBox.x - safeImageCoords.x) / safeImageCoords.width;
      const relativeY = (plateBoundingBox.y - safeImageCoords.y) / safeImageCoords.height;
      const relativeWidth = plateBoundingBox.width / safeImageCoords.width;
      const relativeHeight = plateBoundingBox.height / safeImageCoords.height;

      // Calculate position relative to extended box
      const boxX = ((relativeX - extendedBox.x) / extendedBox.width) * drawWidth;
      const boxY = ((relativeY - extendedBox.y) / extendedBox.height) * drawHeight;
      const boxWidth = (relativeWidth / extendedBox.width) * drawWidth;
      const boxHeight = (relativeHeight / extendedBox.height) * drawHeight;

      // Check if we have valid dimensions
      if (
        isNaN(boxX) ||
        isNaN(boxY) ||
        isNaN(boxWidth) ||
        isNaN(boxHeight) ||
        boxWidth <= 0 ||
        boxHeight <= 0
      ) {
        throw new Error('Invalid bounding box dimensions');
      }

      // Apply the offset to account for uncentered crops
      const finalX = boxX + drawX;
      const finalY = boxY + drawY;

      // Draw the green bounding box
      context.strokeStyle = PlateImageCropService.BOUNDING_BOX_COLOR;
      context.lineWidth = PlateImageCropService.BOUNDING_BOX_LINE_WIDTH;
      context.beginPath();
      context.rect(finalX, finalY, boxWidth, boxHeight);
      context.stroke();
    } catch (error) {
      console.error('Error drawing bounding box:', error);

      // Draw a fallback box in the center of the canvas as a reasonable guess
      const fallbackWidth = drawWidth * 0.6; // 60% of canvas width
      const fallbackHeight = drawHeight * 0.2; // 20% of canvas height, typical license plate aspect ratio
      const fallbackX = (drawWidth - fallbackWidth) / 2;
      const fallbackY = (drawHeight - fallbackHeight) / 2;

      // Draw green bounding box
      context.strokeStyle = PlateImageCropService.BOUNDING_BOX_COLOR;
      context.lineWidth = PlateImageCropService.BOUNDING_BOX_LINE_WIDTH;
      context.beginPath();
      context.rect(fallbackX, fallbackY, fallbackWidth, fallbackHeight);
      context.stroke();
    }
  }

  private _normalizePlateBoundingBox(
    plateBoundingBox: plateCoords,
    imageCoordinates: plateCoords,
    imgWidth: number,
    imgHeight: number,
  ): plateCoords {
    const safeImageCoords = this._createSafeImageCoords(imageCoordinates);
    const safePlateBoundingBox = this._createSafePlateBoundingBox(plateBoundingBox);
    // Step 1: Normalize coordinates
    const normalized = {
      x: (safePlateBoundingBox.x - safeImageCoords.x) / safeImageCoords.width,
      y: (safePlateBoundingBox.y - safeImageCoords.y) / safeImageCoords.height,
      width: safePlateBoundingBox.width * (1.0 / safeImageCoords.width),
      height: safePlateBoundingBox.height * (1.0 / safeImageCoords.height),
    };

    // Verify plate rectangle ratio
    const minRatio = 2.0;
    const imageRatio = imgHeight / imgWidth;

    normalized.width = Math.max(normalized.width, normalized.height * minRatio * imageRatio);

    return normalized;
  }

  private _calculateExtendedBox(normalizedBox: plateCoords): plateCoords {
    // Calculate extended dimensions
    let extendedCropW = normalizedBox.width * PlateImageCropService.EXTEND_FACTOR_WIDTH;
    let extendedCropH = normalizedBox.height * PlateImageCropService.EXTEND_FACTOR_HEIGHT;

    // Apply maximum limits
    extendedCropW = Math.min(PlateImageCropService.MAX_CROP_WIDTH, extendedCropW);
    extendedCropH = Math.min(PlateImageCropService.MAX_CROP_HEIGHT, extendedCropH);

    // Initial calculation of top-left corner
    let extendedCropRectX = normalizedBox.x - (extendedCropW - normalizedBox.width) / 2;
    let extendedCropRectY = normalizedBox.y - (extendedCropH - normalizedBox.height) / 2;

    // Adjust if box goes outside image bounds
    if (extendedCropRectX < 0) {
      extendedCropRectX = 0;
    } else if (extendedCropRectX + extendedCropW > 1) {
      extendedCropRectX = 1 - extendedCropW;
    }

    if (extendedCropRectY < 0) {
      extendedCropRectY = 0;
    } else if (extendedCropRectY + extendedCropH > 1) {
      extendedCropRectY = 1 - extendedCropH;
    }

    return {
      x: extendedCropRectX,
      y: extendedCropRectY,
      width: extendedCropW,
      height: extendedCropH,
    };
  }

  /**
   * Adjusts crop parameters for images that are wider than the canvas
   *
   * @param cropAspectRatio - The aspect ratio of the crop area
   * @param canvas - The target canvas
   * @param cropX - X position of the crop in pixels
   * @param cropY - Y position of the crop in pixels
   * @param cropWidth - Width of the crop in pixels
   * @param cropHeight - Height of the crop in pixels
   * @param imageObj - The source image
   * @param extendedBox - The extended box that will be updated with new coordinates
   * @private
   */
  private _adjustForWideFit(
    cropAspectRatio: number,
    canvas: HTMLCanvasElement,
    cropX: number,
    cropY: number,
    cropWidth: number,
    cropHeight: number,
    imageObj: HTMLImageElement,
    extendedBox: plateCoords,
  ): void {
    // Calculate initial draw dimensions (fitting to width)
    const drawWidth = canvas.width;
    const drawHeight = canvas.width / cropAspectRatio;

    // Calculate additional height needed to fill canvas
    const additionalHeight = (canvas.height - drawHeight) * (cropHeight / drawHeight);
    const halfAdditional = additionalHeight / 2;

    // Calculate extra space on top and bottom
    let topExtra = Math.min(halfAdditional, cropY);
    let bottomExtra = Math.min(halfAdditional, imageObj.height - (cropY + cropHeight));

    // Balance the extra space if one side doesn't have enough
    const balancedExtras = this._balanceVerticalExtras(
      topExtra,
      bottomExtra,
      halfAdditional,
      cropY,
      cropHeight,
      imageObj.height,
    );

    // Update with the balanced values
    topExtra = balancedExtras.topExtra;
    bottomExtra = balancedExtras.bottomExtra;

    // Update the extended box with the new coordinates
    extendedBox.y = (cropY - topExtra) / imageObj.height;
    extendedBox.height = (cropHeight + topExtra + bottomExtra) / imageObj.height;
  }

  /**
   * Adjusts crop parameters for images that are taller than the canvas
   *
   * @param cropAspectRatio - The aspect ratio of the crop area
   * @param canvas - The target canvas
   * @param cropX - X position of the crop in pixels
   * @param cropY - Y position of the crop in pixels
   * @param cropWidth - Width of the crop in pixels
   * @param cropHeight - Height of the crop in pixels
   * @param imageObj - The source image
   * @param extendedBox - The extended box that will be updated with new coordinates
   * @private
   */
  private _adjustForTallFit(
    cropAspectRatio: number,
    canvas: HTMLCanvasElement,
    cropX: number,
    cropY: number,
    cropWidth: number,
    cropHeight: number,
    imageObj: HTMLImageElement,
    extendedBox: plateCoords,
  ): void {
    // Calculate initial draw dimensions (fitting to height)
    const drawHeight = canvas.height;
    const drawWidth = canvas.height * cropAspectRatio;

    // Calculate additional width needed to fill canvas
    const additionalWidth = (canvas.width - drawWidth) * (cropWidth / drawWidth);
    const halfAdditional = additionalWidth / 2;

    // Calculate extra space on left and right
    let leftExtra = Math.min(halfAdditional, cropX);
    let rightExtra = Math.min(halfAdditional, imageObj.width - (cropX + cropWidth));

    // Balance the extra space if one side doesn't have enough
    const balancedExtras = this._balanceHorizontalExtras(
      leftExtra,
      rightExtra,
      halfAdditional,
      cropX,
      cropWidth,
      imageObj.width,
    );

    // Update with the balanced values
    leftExtra = balancedExtras.leftExtra;
    rightExtra = balancedExtras.rightExtra;

    // Update the extended box with the new coordinates
    extendedBox.x = (cropX - leftExtra) / imageObj.width;
    extendedBox.width = (cropWidth + leftExtra + rightExtra) / imageObj.width;
  }

  /**
   * Balances the extra space on left and right sides when one side has insufficient space
   *
   * @param leftExtra - Extra space on the left
   * @param rightExtra - Extra space on the right
   * @param halfAdditional - Half of the additional width needed
   * @param cropX - X position of the crop
   * @param cropWidth - Width of the crop
   * @param imageWidth - Width of the image
   * @private
   * @returns Object with adjusted leftExtra and rightExtra values
   */
  private _balanceHorizontalExtras(
    leftExtra: number,
    rightExtra: number,
    halfAdditional: number,
    cropX: number,
    cropWidth: number,
    imageWidth: number,
  ): { leftExtra: number; rightExtra: number } {
    if (leftExtra < halfAdditional) {
      rightExtra = Math.min(
        halfAdditional - leftExtra + halfAdditional,
        imageWidth - (cropX + cropWidth),
      );
    } else if (rightExtra < halfAdditional) {
      leftExtra = Math.min(halfAdditional - rightExtra + halfAdditional, cropX);
    }

    return { leftExtra, rightExtra };
  }

  /**
   * Balances the extra space on top and bottom when one side has insufficient space
   *
   * @param topExtra - Extra space on the top
   * @param bottomExtra - Extra space on the bottom
   * @param halfAdditional - Half of the additional height needed
   * @param cropY - Y position of the crop
   * @param cropHeight - Height of the crop
   * @param imageHeight - Height of the image
   * @private
   * @returns Object with adjusted topExtra and bottomExtra values
   */
  private _balanceVerticalExtras(
    topExtra: number,
    bottomExtra: number,
    halfAdditional: number,
    cropY: number,
    cropHeight: number,
    imageHeight: number,
  ): { topExtra: number; bottomExtra: number } {
    if (topExtra < halfAdditional) {
      bottomExtra = Math.min(
        halfAdditional - topExtra + halfAdditional,
        imageHeight - (cropY + cropHeight),
      );
    } else if (bottomExtra < halfAdditional) {
      topExtra = Math.min(halfAdditional - bottomExtra + halfAdditional, cropY);
    }

    return { topExtra, bottomExtra };
  }
}
